import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Users, Plus, Trash2, Edit2, CheckCircle2, Clock, Euro, Loader2,
  RefreshCw, TrendingUp, Search, SplitSquareHorizontal, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const PAYMENT_TYPES = { 'gehalt': 'Gehalt', 'bonus': 'Bonus', 'vorschuss': 'Vorschuss', 'abzug': 'Abzug', 'spesen': 'Spesen', 'sonstiges': 'Sonstiges' };

const AdminEmployeePayments = () => {
  const [payments, setPayments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPartialDialog, setShowPartialDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [partialTarget, setPartialTarget] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [partialForm, setPartialForm] = useState({ amount: '', date: new Date().toISOString().split('T')[0], note: '' });
  const [formData, setFormData] = useState({
    employee_id: '', employee_name: '', description: '', amount: '',
    date: new Date().toISOString().split('T')[0], status: 'pending', payment_type: 'gehalt'
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedEmployee !== 'all') params.employee_id = selectedEmployee;
      if (statusFilter !== 'all') params.status = statusFilter;
      const [paymentsRes, summaryRes] = await Promise.all([
        api.get('/admin/employee-payments', { params }),
        api.get('/admin/employee-payments/summary')
      ]);
      setPayments(paymentsRes.data || []);
      setSummary(summaryRes.data || []);
      const empMap = {};
      (summaryRes.data || []).forEach(s => { empMap[s.employee_id] = s.employee_name; });
      (paymentsRes.data || []).forEach(p => { if (p.employee_id && p.employee_name) empMap[p.employee_id] = p.employee_name; });
      setEmployees(Object.entries(empMap).map(([id, name]) => ({ id, name })));
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, [selectedEmployee, statusFilter]);

  const resetForm = () => {
    setFormData({ employee_id: '', employee_name: '', description: '', amount: '', date: new Date().toISOString().split('T')[0], status: 'pending', payment_type: 'gehalt' });
    setEditingPayment(null);
  };

  const openCreateDialog = () => { resetForm(); setShowDialog(true); };

  const openEditDialog = (payment) => {
    setEditingPayment(payment);
    setFormData({
      employee_id: payment.employee_id || '', employee_name: payment.employee_name || '',
      description: payment.description || '', amount: payment.amount?.toString() || '',
      date: payment.date?.split('T')[0] || '', status: payment.status || 'pending',
      payment_type: payment.payment_type || 'gehalt'
    });
    setShowDialog(true);
  };

  const openPartialDialog = (payment) => {
    setPartialTarget(payment);
    const remaining = (payment.amount || 0) - (payment.paid_total || 0);
    setPartialForm({ amount: remaining > 0 ? remaining.toFixed(2) : '', date: new Date().toISOString().split('T')[0], note: '' });
    setShowPartialDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.employee_name || !formData.amount || !formData.date) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    try {
      const data = { ...formData, amount: parseFloat(formData.amount), employee_id: formData.employee_id || formData.employee_name.toLowerCase().replace(/\s+/g, '-') };
      if (editingPayment) {
        await api.put(`/admin/employee-payments/${editingPayment.id}`, data);
        toast.success('Zahlung aktualisiert');
      } else {
        await api.post('/admin/employee-payments', data);
        toast.success('Zahlung erfasst');
      }
      setShowDialog(false); resetForm(); loadData();
    } catch (error) { toast.error('Fehler beim Speichern'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Diese Zahlung wirklich löschen?')) return;
    try { await api.delete(`/admin/employee-payments/${id}`); toast.success('Zahlung gelöscht'); loadData(); }
    catch (error) { toast.error('Fehler beim Löschen'); }
  };

  const handleMarkPaid = async (payment) => {
    try {
      await api.put(`/admin/employee-payments/${payment.id}`, { ...payment, status: 'paid', paid_on: new Date().toISOString() });
      toast.success('Als bezahlt markiert'); loadData();
    } catch (error) { toast.error('Fehler beim Aktualisieren'); }
  };

  const handlePartialPayment = async () => {
    if (!partialForm.amount || parseFloat(partialForm.amount) <= 0) {
      toast.error('Bitte einen gültigen Betrag eingeben'); return;
    }
    try {
      await api.post(`/admin/employee-payments/${partialTarget.id}/partial-payment`, {
        amount: parseFloat(partialForm.amount), date: partialForm.date, note: partialForm.note
      });
      toast.success('Teilzahlung erfasst'); setShowPartialDialog(false); loadData();
    } catch (error) { toast.error('Fehler beim Erfassen der Teilzahlung'); }
  };

  const handleDeletePartial = async (paymentId, partialId) => {
    try {
      await api.delete(`/admin/employee-payments/${paymentId}/partial-payment/${partialId}`);
      toast.success('Teilzahlung gelöscht'); loadData();
    } catch (error) { toast.error('Fehler beim Löschen'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '-';
  const formatCurrency = (a) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(a || 0);

  const totalPending = summary.reduce((sum, s) => sum + (s.pending_total || 0), 0);
  const totalPaid = summary.reduce((sum, s) => sum + (s.paid_total || 0), 0);
  const filteredPayments = payments.filter(p => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (p.employee_name || '').toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-gray-50 -m-4 lg:-m-8 p-4 sm:p-6 overflow-y-auto">
      <div className="max-w-6xl mx-auto mb-6">
        <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5a8e] rounded-xl p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3" data-testid="employee-payments-title"><Users className="w-8 h-8" /> Mitarbeiter-Zahlungen</h1>
              <p className="text-white/80 mt-1">Zahlungen an Mitarbeiter verwalten und nachverfolgen</p>
            </div>
            <Button onClick={openCreateDialog} className="bg-white text-[#1e3a5f] hover:bg-gray-100" data-testid="new-payment-btn"><Plus className="w-4 h-4 mr-2" /> Neue Zahlung</Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center"><Clock className="w-6 h-6 text-yellow-600" /></div><div><p className="text-sm text-gray-500">Ausstehend</p><p className="text-2xl font-bold text-yellow-600" data-testid="pending-total">{formatCurrency(totalPending)}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center"><TrendingUp className="w-6 h-6 text-green-600" /></div><div><p className="text-sm text-gray-500">Bezahlt (Gesamt)</p><p className="text-2xl font-bold text-green-600" data-testid="paid-total">{formatCurrency(totalPaid)}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center"><Users className="w-6 h-6 text-blue-600" /></div><div><p className="text-sm text-gray-500">Mitarbeiter</p><p className="text-2xl font-bold text-blue-600" data-testid="employee-count">{summary.length}</p></div></CardContent></Card>
        </div>
      </div>

      {summary.length > 0 && (
        <div className="max-w-6xl mx-auto mb-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Mitarbeiter-Übersicht</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {summary.map((emp) => (
                  <div key={emp.employee_id} onClick={() => setSelectedEmployee(selectedEmployee === emp.employee_id ? 'all' : emp.employee_id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${selectedEmployee === emp.employee_id ? 'border-[#1e3a5f] bg-[#1e3a5f]/5 ring-1 ring-[#1e3a5f]' : 'border-gray-200 hover:border-gray-300'}`}
                    data-testid={`employee-card-${emp.employee_id}`}>
                    <p className="font-medium text-gray-900">{emp.employee_name}</p>
                    <div className="flex justify-between mt-2 text-sm">
                      <span className="text-green-600">Bezahlt: {formatCurrency(emp.paid_total)}</span>
                      <span className="text-yellow-600">Offen: {formatCurrency(emp.pending_total)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="max-w-6xl mx-auto mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input placeholder="Suche..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="search-payments" /></div>
          <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">Alle Status</SelectItem><SelectItem value="pending">Ausstehend</SelectItem><SelectItem value="partial">Teilbezahlt</SelectItem><SelectItem value="paid">Bezahlt</SelectItem></SelectContent></Select>
          <Button variant="ghost" size="icon" onClick={loadData} data-testid="refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{selectedEmployee !== 'all' ? `Zahlungen für ${employees.find(e => e.id === selectedEmployee)?.name || ''}` : 'Alle Zahlungen'}</CardTitle>
            {selectedEmployee !== 'all' && <Button variant="ghost" size="sm" onClick={() => setSelectedEmployee('all')}>Alle anzeigen</Button>}
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center p-8"><Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" /></div>
            ) : filteredPayments.length === 0 ? (
              <div className="text-center py-8 text-gray-500"><Euro className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p>Keine Zahlungen gefunden</p><Button variant="outline" className="mt-3" onClick={openCreateDialog}><Plus className="w-4 h-4 mr-2" /> Erste Zahlung erfassen</Button></div>
            ) : (
              <div className="space-y-3">
                {filteredPayments.map((payment) => {
                  const partials = payment.partial_payments || [];
                  const paidTotal = payment.paid_total || 0;
                  const remaining = (payment.amount || 0) - paidTotal;
                  const isExpanded = expandedId === payment.id;

                  return (
                    <div key={payment.id} className={`rounded-lg border ${payment.status === 'paid' ? 'bg-green-50/50 border-green-200' : payment.status === 'partial' ? 'bg-orange-50/50 border-orange-200' : 'bg-gray-50 border-gray-200'}`} data-testid={`payment-item-${payment.id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4">
                        <div className="flex-1 mb-3 sm:mb-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-gray-900">{payment.employee_name}</h3>
                            <span className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-600">{PAYMENT_TYPES[payment.payment_type] || payment.payment_type}</span>
                            {payment.status === 'paid' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3" /> Bezahlt</span>
                            ) : payment.status === 'partial' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700"><SplitSquareHorizontal className="w-3 h-3" /> Teilbezahlt</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3" /> Ausstehend</span>
                            )}
                          </div>
                          {payment.description && <p className="text-sm text-gray-500 mb-1">{payment.description}</p>}
                          <div className="flex flex-wrap gap-3 text-sm">
                            <span className="text-gray-400">Datum: {formatDate(payment.date)}</span>
                            {paidTotal > 0 && payment.status !== 'paid' && <span className="text-orange-600 font-medium">Bezahlt: {formatCurrency(paidTotal)} / Rest: {formatCurrency(remaining)}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <span className={`text-xl font-bold ${payment.payment_type === 'abzug' ? 'text-red-600' : 'text-[#1e3a5f]'}`}>{payment.payment_type === 'abzug' ? '-' : ''}{formatCurrency(payment.amount)}</span>
                            {paidTotal > 0 && payment.status !== 'paid' && <p className="text-xs text-orange-600">Rest: {formatCurrency(remaining)}</p>}
                          </div>
                          <div className="flex gap-1">
                            {payment.status !== 'paid' && (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => openPartialDialog(payment)} title="Teilzahlung" className="text-orange-600 hover:text-orange-700 hover:bg-orange-50" data-testid={`partial-payment-${payment.id}`}>
                                  <SplitSquareHorizontal className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleMarkPaid(payment)} title="Voll bezahlt" className="text-green-600 hover:text-green-700 hover:bg-green-50" data-testid={`mark-paid-${payment.id}`}>
                                  <CheckCircle2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(payment)} data-testid={`edit-payment-${payment.id}`}><Edit2 className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(payment.id)} className="text-red-500 hover:text-red-600" data-testid={`delete-payment-${payment.id}`}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                          {partials.length > 0 && (
                            <Button variant="ghost" size="sm" onClick={() => setExpandedId(isExpanded ? null : payment.id)}>
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </Button>
                          )}
                        </div>
                      </div>
                      {isExpanded && partials.length > 0 && (
                        <div className="border-t bg-white/50 px-4 py-3">
                          <p className="text-xs font-semibold text-gray-500 mb-2">Teilzahlungen:</p>
                          <div className="space-y-1.5">
                            {partials.map((p) => (
                              <div key={p.id} className="flex items-center justify-between text-sm bg-orange-50 rounded px-3 py-1.5">
                                <div className="flex items-center gap-3">
                                  <span className="font-medium text-orange-700">{formatCurrency(p.amount)}</span>
                                  <span className="text-gray-500">{formatDate(p.date)}</span>
                                  {p.note && <span className="text-gray-400">- {p.note}</span>}
                                </div>
                                {payment.status !== 'paid' && (
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => handleDeletePartial(payment.id, p.id)}>
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingPayment ? 'Zahlung bearbeiten' : 'Neue Zahlung erfassen'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Mitarbeiter-Name *</Label><Input value={formData.employee_name} onChange={(e) => setFormData({ ...formData, employee_name: e.target.value })} placeholder="z.B. Max Mustermann" className="mt-1" list="employee-names" data-testid="input-employee-name" /><datalist id="employee-names">{employees.map(emp => <option key={emp.id} value={emp.name} />)}</datalist></div>
            <div><Label>Beschreibung</Label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="z.B. Gehalt Februar 2026" className="mt-1" data-testid="input-description" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Betrag *</Label><Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="0.00" className="mt-1" data-testid="input-amount" /></div>
              <div><Label>Art</Label><Select value={formData.payment_type} onValueChange={(v) => setFormData({ ...formData, payment_type: v })}><SelectTrigger className="mt-1" data-testid="select-payment-type"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(PAYMENT_TYPES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Datum *</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className="mt-1" data-testid="input-date" /></div>
              <div><Label>Status</Label><Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}><SelectTrigger className="mt-1" data-testid="select-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="pending">Ausstehend</SelectItem><SelectItem value="paid">Bezahlt</SelectItem></SelectContent></Select></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Abbrechen</Button>
            <Button onClick={handleSubmit} className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white" data-testid="submit-payment-btn">{editingPayment ? 'Speichern' : 'Erfassen'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partial Payment Dialog */}
      <Dialog open={showPartialDialog} onOpenChange={setShowPartialDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Teilzahlung erfassen</DialogTitle></DialogHeader>
          {partialTarget && (
            <div className="space-y-4 py-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-gray-900">{partialTarget.employee_name} - {partialTarget.description || PAYMENT_TYPES[partialTarget.payment_type]}</p>
                <p className="text-gray-600">Gesamtbetrag: {formatCurrency(partialTarget.amount)}</p>
                {(partialTarget.paid_total || 0) > 0 && <p className="text-orange-600">Bereits bezahlt: {formatCurrency(partialTarget.paid_total)}</p>}
                <p className="font-semibold text-[#1e3a5f]">Restbetrag: {formatCurrency((partialTarget.amount || 0) - (partialTarget.paid_total || 0))}</p>
              </div>
              <div><Label>Betrag der Teilzahlung *</Label><Input type="number" step="0.01" value={partialForm.amount} onChange={(e) => setPartialForm({ ...partialForm, amount: e.target.value })} className="mt-1" data-testid="partial-amount" /></div>
              <div><Label>Datum *</Label><Input type="date" value={partialForm.date} onChange={(e) => setPartialForm({ ...partialForm, date: e.target.value })} className="mt-1" data-testid="partial-date" /></div>
              <div><Label>Notiz (wo/wie bezahlt)</Label><Input value={partialForm.note} onChange={(e) => setPartialForm({ ...partialForm, note: e.target.value })} placeholder="z.B. Bar, Überweisung..." className="mt-1" data-testid="partial-note" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPartialDialog(false)}>Abbrechen</Button>
            <Button onClick={handlePartialPayment} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="submit-partial-btn">Teilzahlung erfassen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEmployeePayments;
